import React, { useEffect, useState } from "react";
import { Box, Tab, Tabs, Button, Tooltip, Grid } from "@mui/material";
import Loader from "../Molecules/Loader/Loader";
import DashboardUserDetails from "../Molecules/Rule/DashboardUserDetails";
import IncidentUserandStatusDialog from "../Molecules/Rule/IncidentUserandStatusDialog";
import useFetchUsersList from "../../hooks/useFetchUsersList";
import RootCauseAnalysisDataGrid from "../DataGrids/RootCauseAnalysisDataGrid";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-pro";
import { useClassificationContext } from "../../contexts/ClassificationContext";
import useFetchAllClassifications from "../../hooks/useFetchAllClassifications";

export interface IResearchQueryResultsWithIssues {
  isLoading: boolean;
  isFilterHide: boolean;
  selectedQueryName: any;
  setSelectedQueryName: React.Dispatch<React.SetStateAction<any>>;
  queryTabs: any;
  pageN: number;
  setPageN: React.Dispatch<React.SetStateAction<any>>;
  pageS: number;
  setPageS: React.Dispatch<React.SetStateAction<any>>;
  totalPages: number;
  refetchResearchIssues: () => void;
  columnsData: any;
  rowsData: any;
  setDashboardUserDetailsData: React.Dispatch<React.SetStateAction<any>>;
  dashboardUserDetailsData: any;
  setUserDialogData: React.Dispatch<React.SetStateAction<any>>;
  userDialogData: any;
  setIsLoading: any;
  isIssuesDataAvailable?: any;
  errorMessage?: string | null;
}

/**
 * ResearchQueryResultsWithIssues component for displaying paginated research query results
 *
 * @param isLoading - Loading state
 * @param isFilterHide - Filter visibility state
 * @param selectedQueryName - Selected query name
 * @param setSelectedQueryName - Function to set selected query name
 * @param queryTabs - Query tabs
 * @param pageN - Current page number
 * @param setPageN - Function to set page number
 * @param pageS - Page size
 * @param setPageS - Function to set page size
 * @param totalPages - Total number of pages
 * @param refetchResearchIssues - Function to refetch research issues data
 * @param columnsData - Columns data for the data grid
 * @param rowsData - Rows data for the data grid
 * @param setDashboardUserDetailsData - Function to set dashboard user details data
 * @param dashboardUserDetailsData - Dashboard user details data
 * @param setUserDialogData - Function to set user dialog data
 * @param userDialogData - User dialog data
 */
const ResearchQueryResultsWithIssues = ({
  isLoading,
  setIsLoading,
  isFilterHide,
  selectedQueryName,
  setSelectedQueryName,
  queryTabs,
  pageN,
  setPageN,
  pageS,
  setPageS,
  totalPages,
  columnsData,
  rowsData,
  setDashboardUserDetailsData,
  dashboardUserDetailsData,
  setUserDialogData,
  userDialogData,
  refetchResearchIssues,
  isIssuesDataAvailable,
  errorMessage,
}: IResearchQueryResultsWithIssues) => {
  // const [selectedQueryTab, setSelectedQueryTab] = useState<any>(0);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [isResearchIssuesLoading, setIsResearchIssuesLoading] =
    useState<boolean>(false);
  const [pinnedColumns, setPinnedColumns] = useState({
    left: isIssuesDataAvailable ? [GRID_CHECKBOX_SELECTION_COL_DEF.field] : [],
    right: isIssuesDataAvailable
      ? ["current_status", "username", "comment", "action"]
      : [],
  });
  const [isUserLoading, setIsUserLoading] = useState(false);

  // Fetch users list for assignment
  const [allUsersList] = useFetchUsersList({
    setIsLoading: setIsUserLoading,
  });

  // Fetch classifications once and store in shared context (same pattern as TagAndSystemProvider)
  const { setAllClassifications, setIsClassificationsLoading } =
    useClassificationContext();
  const [fetchedClassifications] = useFetchAllClassifications({
    setIsLoading: setIsClassificationsLoading,
  });

  useEffect(() => {
    if (fetchedClassifications.length > 0) {
      setAllClassifications(fetchedClassifications);
    }
  }, [fetchedClassifications, setAllClassifications]);

  // Update pinned columns when isIssuesDataAvailable changes
  React.useEffect(() => {
    setPinnedColumns({
      left: isIssuesDataAvailable
        ? [GRID_CHECKBOX_SELECTION_COL_DEF.field]
        : [],
      right: isIssuesDataAvailable
        ? ["current_status", "username", "comment", "action"]
        : [],
    });
  }, [isIssuesDataAvailable]);

  const handleChangeQueryTab = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setSelectedQueryName(newValue);
    // Enable loader for subsequent tab changes
    // Reset selected IDs when changing tabs
    setSelectedIds([]);
  };

  return (
    <>
      {/* Mass Update Button */}

      <Grid
        container
        rowSpacing={0}
        columnSpacing={0}
        className="mui-parent-border-right"
      >
        <Grid item xs={rowsData?.length > 0 && isIssuesDataAvailable ? 10 : 12}>
          <Tabs
            value={selectedQueryName}
            onChange={handleChangeQueryTab}
            textColor="secondary"
            indicatorColor="secondary"
            className={`mui-tabs no-t-lr-radius min-height-0 alternative-1 ${
              rowsData?.length > 0 && isIssuesDataAvailable
                ? "no-right-bdr"
                : ""
            }`}
            variant="scrollable"
            scrollButtons="auto"
          >
            {queryTabs?.length > 0 &&
              queryTabs?.map((item: any, index: number) => (
                <Tab key={index} value={item?.name} label={item?.name} />
              ))}
          </Tabs>
        </Grid>
        {rowsData?.length > 0 && isIssuesDataAvailable && (
          <Grid
            item
            xs={2}
            className="d-flex justify-content-end align-items-center"
          >
            <Box className="mui-update-issue">
              <Button
                className="btn-blue btn-orange btn-sm"
                onClick={() => {
                  setUserDialogData({
                    status: true,
                    dialogName: "Mass Update Issues",
                    selectedIds: selectedIds,
                  });
                }}
                disabled={selectedIds.length === 0}
              >
                Update Issue
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
      {
        <Box className="position-relative">
          {(isLoading || isResearchIssuesLoading || isUserLoading) && (
            <Loader
              isAbsolute={true}
              radius={{ top: 0, right: 0, bottom: 5, left: 5 }}
              isLoading={isLoading || isResearchIssuesLoading || isUserLoading}
            />
          )}

          <RootCauseAnalysisDataGrid
            dataRows={rowsData || []}
            dataColumns={columnsData || []}
            loading={isLoading}
            className="dataTable filterMenuBox filterMenuBox1 no-radius pt-0 bdr-top-0 hide-progress-icon datatable-column-sep"
            disableColumnFilter={true}
            isFilterHide={isFilterHide}
            tableHeight={180}
            paginationMode="server"
            rowCount={totalPages || 0}
            paginationModel={{
              page: pageN - 1,
              pageSize: pageS,
            }}
            onPaginationModelChange={(params: any) => {
              if (params.pageSize !== pageS || params.page !== pageN - 1) {
                setPageN(params.page + 1);
                setPageS(params.pageSize);
              }
            }}
            checkboxSelection={
              rowsData?.length > 0 && isIssuesDataAvailable ? true : false
            }
            rowSelectionModel={selectedIds}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            pinnedColumns={rowsData?.length > 0 ? pinnedColumns : {}}
            setPinnedColumns={setPinnedColumns}
            disableColumnPinning={false}
            isIssuesDataAvailable={isIssuesDataAvailable}
            errorMsg={errorMessage}
          />
        </Box>
      }

      {/* Issue Management Dialogs */}
      {isIssuesDataAvailable && (
        <>
          <DashboardUserDetails
            dashboardUserDetailsData={dashboardUserDetailsData}
            setDashboardUserDetailsData={setDashboardUserDetailsData}
            allUsersList={allUsersList}
            setIsLoading={setIsResearchIssuesLoading}
            setDetailViewDataRows={() => {
              // Refetch data after update
              refetchResearchIssues();
            }}
            issueFrom="ruleResearch"
          />
          <IncidentUserandStatusDialog
            userDialogData={userDialogData}
            setUserDialogData={setUserDialogData}
            allUsersList={allUsersList}
            setDetailViewDataRows={() => {
              // Refetch data after update
              refetchResearchIssues();
            }}
            setSelectedIds={setSelectedIds}
            issueFrom="ruleResearch"
          />
        </>
      )}
    </>
  );
};

export default ResearchQueryResultsWithIssues;
